/* Shared measurement parsing for storefront and server. No network or paid requests. */
var LondaFitModel = (() => {
  'use strict';
  const measure=value=>{const s=String(value??'').normalize('NFKC').trim().replace(/cm|㎝/gi,'').trim(),m=s.match(/^(\d+(?:\.\d+)?)(?:\s*[-~〜～–—−ー]\s*(\d+(?:\.\d+)?))?$/);if(!m)return null;const min=Number(m[1]),max=Number(m[2]||m[1]);return min>0&&max>=min?{min,max}:null;};
  // Use the displayed chart heading. Some corrected charts retain an obsolete sourceLabel.
  const col=(table,re)=>table.columns.findIndex(c=>re.test(String([c.ja,c.en,c.sourceLabel].find(v=>String(v||'').trim())||'').trim()));
  function rowsFrom(chart){
    if(chart?.unit!=='cm'||!['tops','outerwear','coat','jacket','dress','bottoms'].includes(chart.type)||!Array.isArray(chart.tables)||chart.tables.length!==1)return [];
    const t=chart.tables[0];if(!Array.isArray(t.columns)||!Array.isArray(t.rows))return [];
    const columns={chest:col(t,/^(胸囲|バスト|bust|chest|chest circumference)$/i),width:col(t,/^(身幅|chest width)$/i),length:col(t,/^(着丈|衣丈|総丈|length|body length|total length)$/i),shoulder:col(t,/^(肩幅|shoulder|shoulder width)$/i),sleeve:col(t,/^(袖丈|袖長|sleeve length)$/i),waist:col(t,/^(ウエスト|ウェスト|waist)$/i),hip:col(t,/^(ヒップ|hip|hips)$/i),inseam:col(t,/^(股下|inseam)$/i)};
    return t.rows.map(r=>{
      const v={};for(const k in columns)v[k]=columns[k]>=0?measure(r[columns[k]]):null;
      const chest=v.chest||(v.width?{min:v.width.min*2,max:v.width.max*2}:null);
      const row={size:String(r[0]||'').trim(),kind:chart.type,chest:chest?.min||null,chestMax:chest?.max||null};
      for(const name of ['length','shoulder','sleeve','waist','hip','inseam']){
        row[name]=v[name]?.min||null;row[name+'Max']=v[name]?.max||null;
      }
      return row;
    }).filter(r=>r.size.length>0&&r.size.length<32&&r.length>=15&&r.lengthMax<=170&&(r.kind==='bottoms'?r.waist>=40&&r.waistMax<=180&&r.hip>=55&&r.hipMax<=200:r.chest>=55&&r.chestMax<=220));
  }
  const round=v=>Math.round(v*10)/10;
  function body(h,w,values={}){
    const delta=w-21*(h/100)**2,clamp=(v,a,b)=>Math.min(b,Math.max(a,v));
    return {height:h,weight:w,bust:round(Number(values.bust)||clamp(h*.5+6+delta*.65,60,175)),shoulder:round(Number(values.shoulder)||clamp(h*.23+delta*.08,26,64)),waist:round(Number(values.waist)||clamp(h*.44-4+delta*.8,45,170)),hip:round(Number(values.hip)||clamp(h*.55+5+delta*.65,60,190))};
  }
  function dimension(row,name){
    const min=row?.[name],max=row?.[name+'Max']??min;
    return Number.isFinite(min)&&Number.isFinite(max)&&min>0&&max>=min?{min,max}:null;
  }
  const middle=range=>(range.min+range.max)/2;
  const canonical=value=>String(value??'').normalize('NFKC').toUpperCase().replace(/\s|サイズ/g,'').replace(/^(?:ONESIZE|FREESIZE|FREE|F|フリー|ワンサイズ)$/,'FREE');
  function modelReference(value,rows=[]){
    const text=String(value??'').normalize('NFKC');
    const height=text.match(/(?:モデル身長|身長|model\s*height|height)\s*[:：]?\s*(\d+(?:\.\d+)?)\s*cm/i);
    const size=text.match(/(?:着用サイズ|着用|wearing\s*size|worn\s*size|model\s*size|wears(?:\s*size)?)\s*[:：]?\s*([A-Za-z0-9][A-Za-z0-9 .-]*|フリー(?:サイズ)?|ワンサイズ)/i);
    if(!height||!size)return null;
    const row=rows.find(r=>canonical(r.size)===canonical(size[1]));
    const h=Number(height[1]);
    // A listed height and worn size are a reference, never an inferred model weight.
    return row&&h>=130&&h<=220?{height:h,size:row.size,weight:null}:null;
  }
  function rank(rows,b,fit='regular',reference=null){
    if(!b||!['height','weight','bust','shoulder','waist','hip'].every(k=>Number.isFinite(b[k])&&b[k]>0))return [];
    if(b.height<130||b.height>220||b.weight<30||b.weight>200)return [];
    const target={slim:12,regular:20,loose:30}[fit]??20,minEase={slim:6,regular:10,loose:16}[fit]??10;
    return rows.map(r=>{
      let ok=true,score=0,caution=false;
      if(r.kind==='bottoms'){
        const waist=dimension(r,'waist'),hip=dimension(r,'hip');
        if(!waist||!hip)return {r,score:Infinity,ok:false};
        const waistRange=waist.max>waist.min,hipRange=hip.max>hip.min;
        // Estimated body measurements are not a tape measurement. Borderline
        // differences remain candidates with a confirmation note, not a fit promise.
        ok=b.waist-waist.max<=Math.max(10,b.waist*.16)&&b.hip-hip.max<=Math.max(12,b.hip*.15);
        caution=b.waist>waist.max-2||b.waist<waist.min-(waistRange?8:4)||hip.max-b.hip<2;
        score=Math.abs((waistRange?middle(waist):waist.min)-b.waist-(waistRange?0:2))+Math.abs((hipRange?middle(hip):hip.min)-b.hip-({slim:4,regular:8,loose:14}[fit]||8))*.4;
      }else{
        const chest=dimension(r,'chest'),waist=dimension(r,'waist'),hip=dimension(r,'hip'),shoulder=dimension(r,'shoulder');
        if(!chest)return {r,score:Infinity,ok:false};
        const ranged=chest.max>chest.min,dress=r.kind==='dress';
        // Range endpoints remain published product dimensions. Do not treat the minimum
        // as the only circumference or the maximum as guaranteed comfortable stretch.
        const needed=ranged?({slim:1,regular:2,loose:4}[fit]??2):dress?({slim:3,regular:6,loose:12}[fit]??6):minEase;
        const desired=ranged?({slim:1,regular:4,loose:8}[fit]??4):dress?target*.6:target;
        ok=b.bust-chest.max<=Math.max(12,b.bust*.15)&&(!waist||b.waist-waist.max<=Math.max(10,b.waist*.16))&&(!hip||b.hip-hip.max<=Math.max(12,b.hip*.15));
        caution=chest.max-b.bust<needed||Boolean(shoulder&&shoulder.max<b.shoulder-1)||Boolean(waist&&b.waist>waist.max-2)||Boolean(hip&&b.hip>hip.max-2);
        score=Math.abs((ranged?middle(chest):chest.min)-b.bust-desired)+Math.abs(r.length-b.height*(dress?.68:.42))*.1+(shoulder?Math.abs(middle(shoulder)-b.shoulder-(fit==='loose'?5:2))*.05:0);
      }
      // A small tie-break among feasible choices only. Matching height never overrides
      // the circumference checks, and the model's unknown weight is not fabricated.
      if(ok&&reference&&canonical(reference.size)===canonical(r.size)&&Math.abs(reference.height-b.height)<=5){
        score-=2*(1-Math.abs(reference.height-b.height)/6);
      }
      return {r,score,ok,caution};
    }).sort((a,b)=>Number(b.ok)-Number(a.ok)||a.score-b.score);
  }
  function recommend(rows,b,fit,reference){return rank(rows,b,fit,reference).find(x=>x.ok)?.r||null;}
  return {measure,rowsFrom,body,dimension,modelReference,rank,recommend};
})();


/* Public product data only. Unknown measurements are never inferred as product facts. */
var LondaFitCatalog = (() => {
  const text=v=>String(v??'').normalize('NFKC').trim();
  const heading=c=>text(c?.ja||c?.en||c?.sourceLabel);
  const apparel=/\b(?:shirt|blouse|top|jacket|blouson|coat|dress|skirt|pants|trousers|shorts|denim|jeans|vest|cardigan|sweater|sweatshirt|hoodie|hooded|pullover|jumpsuit|romper|salopette|overalls|tunic|leggings|tights|camisole|bustier|corset|poncho|cape|gilet|co-ord)\b|ワンピース|ジャケット|コート|ブラウス|パンツ|スカート|トップス|カーディガン/i;
  const accessory=/\b(?:bags?|handbags?|totes?|pouches?|wallets?|belts?|hats?|caps?|berets?|gloves?|socks?|shoes?|boots?|sandals?|sneakers?|loafers?|heels?|earrings?|necklaces?|rings?|bracelets?|brooches?|collars?|scarves?|scarf|shawls?|ties?|sunglasses|eyewear|cuffs?)\b|バッグ|靴|帽子|ベルト|手袋|靴下|アクセサリー/i;
  function isApparel(chart,title,tags=[]){
    if(['bags','shoes'].includes(chart?.type))return false;
    if(apparel.test(text(title))||/\b(?:slacks|tee|t-shirt|tank|cami)\b/i.test(text(title)))return true;
    const hasGarmentMeasurements=(chart?.tables||[]).some(t=>(t.columns||[]).some(c=>/^(胸囲|バスト|身幅|ヒップ|bust|chest|hip|hips)$/i.test(heading(c))));
    if(hasGarmentMeasurements)return true;
    if(accessory.test(text(title)))return false;
    if(tags.some(t=>/^(トップス|セットアップ|カットソー|パンツ|スカート|アウター|ジャケット|コート|ワンピース)/.test(text(t))))return true;
    return ['tops','outerwear','coat','jacket','dress','bottoms','multi'].includes(chart?.type);
  }
  const sizeOf=v=>text(v?.options?.find(o=>/size|サイズ/i.test(o.name))?.value)||'FREE';
  const sizingLabel=v=>text(v).replace(/\s*[・･]\s*(?:即日発送|通常発送)\s*$/,'');
  const canonical=v=>sizingLabel(v).toUpperCase().replace(/\s/g,'').replace(/^(?:ONESIZE|FREESIZE|FREE|F|OS|OSFA|フリーサイズ|フリー|ワンサイズ)$/,'FREE');
  function variantForSize(variants,size,currentVariantId){
    if(!Array.isArray(variants)||!variants.length)return null;
    const current=currentVariantId?variants.find(v=>String(v.id)===String(currentVariantId)):variants[0];
    if(!current)return null;
    const nonSize=v=>(v.options||[]).filter(o=>!/size|サイズ/i.test(o.name)).map(o=>[text(o.name).toLowerCase(),text(o.value)]).sort((a,b)=>a[0].localeCompare(b[0]));
    const options=JSON.stringify(nonSize(current));
    // Never silently change color, sleeve choice, or another non-size option.
    return variants.find(v=>canonical(sizeOf(v))===canonical(size)&&JSON.stringify(nonSize(v))===options)||null;
  }
  function selection(guide,variant){
    const opts=(variant?.options||[]).map(o=>text(o.value)),size=sizeOf(variant);
    const tables=(guide?.tables||[]).map((t,index)=>({t,index,label:text(t.titleJa||t.titleEn)||heading(t.columns?.[0])}));
    // Style choices such as short/long sleeves must not be merged into one garment.
    const matched=tables.filter(x=>opts.some(v=>v===x.label));
    const chosen=matched.length?matched:tables;
    const parts=[];
    for(const {t,index,label} of chosen){
      const rows=(t.rows||[]).filter(r=>canonical(r[0])===canonical(size));
      if(rows.length!==1)continue;
      const dimensions=[];
      (t.columns||[]).slice(1).forEach((col,i)=>{const value=text(rows[0][i+1]);if(value&&value!=='-'&&value!=='—')dimensions.push({label:heading(col).slice(0,80),value:value.slice(0,100)});});
      if(dimensions.length)parts.push({table:index,label:label.slice(0,80),size:text(rows[0][0]),dimensions});
    }
    return {variantId:String(variant.id),variantTitle:text(variant.title).slice(0,180),size,unit:text(guide?.unit)||null,parts};
  }
  const normalizedHeading=value=>text(value).replace(/[（(][^）)]*[）)]/g,'').replace(/\s/g,'').toLowerCase();
  const fieldPatterns={
    chest:/^(胸囲|バスト|bust|chest|chestcircumference)$/,
    width:/^(身幅|胸幅|chestwidth)$/,
    waist:/^(ウエスト|ウェスト|waist)$/,
    hip:/^(ヒップ|hip|hips)$/,
    length:/^(着丈|衣丈|総丈|length|bodylength|totallength|スカート部分総丈)$/,
    shoulder:/^(肩幅|shoulder|shoulderwidth)$/,
    sleeve:/^(袖丈|袖長|sleevelength)$/,
    inseam:/^(股下|inseam)$/,
    height:/^(身長|適応身長|height|recommendedheight)$/
  };
  const mid=r=>(r.min+r.max)/2;
  const primaryNames=['chest','waist','hip'];
  function parsedRow(table,raw,chart,title){
    const values={},elastic={};
    for(let i=1;i<(table.columns||[]).length;i++){
      const label=heading(table.columns[i]),key=Object.keys(fieldPatterns).find(k=>fieldPatterns[k].test(normalizedHeading(label)));
      if(!key||values[key])continue;
      const r=LondaFitModel.measure(raw[i]);
      if(!r)continue;
      // Discard plainly malformed cells, without discarding the rest of the row.
      const valid=primaryNames.includes(key)?r.min>=35&&r.max<=400:key==='width'?r.min>=17.5&&r.max<=200:r.min>0&&r.max<=250;
      if(valid){values[key]=r;elastic[key]=/ゴム|elastic|stretch/i.test(label);}
    }
    if(!values.chest&&values.width)values.chest={min:values.width.min*2,max:values.width.max*2};
    const label=text(table.titleJa||table.titleEn)||heading(table.columns?.[0]);
    let kind=table.type||chart?.type||'tops';
    if(!values.chest&&(values.waist||values.hip))kind='bottoms';
    else if(values.chest){
      const topPart=/トップス|シャツ|ブラウス|ベスト|ジャケット|コート|カーディガン|セーター|インナー|tops?|shirts?|blouse|vest|jacket|coat|cardigan|sweater|inner/i.test(label);
      kind=!topPart&&/ワンピース|ドレス|\bdress\b/i.test(label+' '+title)?'dress':'tops';
    }else if(/パンツ|スカート|ショーツ|\b(pants|trousers|skirt|shorts|tights)\b/i.test(label+' '+title))kind='bottoms';
    else if(/ワンピース|ドレス|\bdress\b/i.test(label+' '+title))kind='dress';
    const row={size:text(raw[0]),kind,elastic};
    for(const key of ['chest','waist','hip','length','shoulder','sleeve','inseam','height']){
      row[key]=values[key]?.min??null;row[key+'Max']=values[key]?.max??null;
    }
    return row;
  }
  function tablesForVariant(chart,variant){
    // Self-contained so the image service can use precisely the same part selector.
    const clean=v=>String(v??'').normalize('NFKC').trim(),norm=v=>clean(v).toLowerCase().replace(/\s/g,'');
    const sizeKey=v=>norm(v).replace(/[・･](?:即日発送|通常発送)$/,'').replace(/^(?:onesize|freesize|free|f|os|osfa|フリーサイズ|フリー|ワンサイズ)$/,'free');
    const head=c=>clean(c?.ja||c?.en||c?.sourceLabel);
    const options=(variant?.options||[]).filter(o=>!/size|サイズ/i.test(o.name));
    const tables=(chart?.tables||[]).map((table,index)=>({table,index,label:clean(table.titleJa||table.titleEn)||head(table.columns?.[0])}));
    if(tables.length<2)return tables;
    const style=options.find(o=>/style|type|スタイル|タイプ|sleeve|length|袖|丈/i.test(o.name))?.value;
    const bundle=/セット|set(?:up)?|co-?ord|[+＋&＆]/i.test(clean(style));
    if(/セット|^(?:set|setup|co-?ord)$/i.test(clean(style)))return tables;
    const axis=v=>{
      const s=norm(v);
      if(/ショートスリーブ|半袖|shortsleeve/.test(s))return 'sleeve-short';
      if(/ロングスリーブ|長袖|longsleeve/.test(s))return 'sleeve-long';
      if(/クロップド|cropped/.test(s))return 'cropped';
      if(/ショート|short/.test(s))return 'short';
      if(/ロング|long/.test(s))return 'long';
      return '';
    };
    const category=(v,table)=>{
      const s=norm(v);
      if(/スカート|skirt/.test(s))return 'skirt';
      if(/パンツ|pants|trousers|shorts/.test(s))return 'pants';
      if(/ワンピース|ドレス|onepiece|dress/.test(s))return 'dress';
      if(/ベスト|vest|gilet/.test(s))return 'vest';
      if(/ジャケット|jacket|blazer/.test(s))return 'jacket';
      if(/コート|coat/.test(s))return 'coat';
      if(/ブラウス|blouse/.test(s))return 'blouse';
      if(/シャツ|shirts?/.test(s))return 'shirt';
      if(/トップ|top|セーター|sweater|カーディガン|cardigan/.test(s))return 'top';
      if(/ショール|マフラー|付け襟|scarf|shawl|collar/.test(s))return 'accessory';
      const columns=(table?.columns||[]).map(head).join('|');
      if(/胸囲|バスト|身幅|bust|chest/i.test(columns))return 'top';
      if(/ウエスト|ウェスト|ヒップ|waist|hip/i.test(columns))return 'bottom';
      return '';
    };
    const family=k=>['top','vest','jacket','coat','blouse','shirt'].includes(k)?'top':['pants','skirt','bottom'].includes(k)?'bottom':k;
    let chosen=tables.filter(x=>options.some(o=>norm(o.value)===norm(x.label)));
    if(!chosen.length&&style){
      const desiredAxis=axis(style);
      if(desiredAxis)chosen=tables.filter(x=>axis(x.label)===desiredAxis);
      if(!chosen.length){
        const desiredCategories=clean(style).split(/[+＋&＆]/).map(v=>category(v)).filter(Boolean);
        chosen=tables.filter(x=>desiredCategories.includes(category(x.label,x.table)));
        if(!chosen.length)chosen=tables.filter(x=>desiredCategories.some(k=>family(k)===family(category(x.label,x.table))));
      }
      // An unknown separate-sale type must not inherit the measurements of every other type.
      if(!chosen.length)return [];
    }
    if(!chosen.length)chosen=tables;
    if(bundle&&/付け襟|collar/i.test(clean(style))){
      for(const item of tables)if(!chosen.includes(item)&&!category(item.label,item.table)&&(item.table.rows||[]).length===1&&sizeKey(item.table.rows[0][0])==='free')chosen.push(item);
    }
    const colors=v=>{
      const s=norm(v),out=[];
      for(const [key,re] of [['black',/black|ブラック|黒/],['blue',/blue|ブルー|青/],['brown',/brown|ブラウン|茶/],['red',/red|レッド|赤/],['white',/white|ホワイト|白/],['gray',/gray|grey|グレー/],['pink',/pink|ピンク/],['purple',/purple|パープル|紫/],['green',/green|グリーン|緑/],['yellow',/yellow|イエロー|黄/]])if(re.test(s))out.push(key);
      return out;
    };
    const currentColors=options.filter(o=>/colou?r|カラー|色/i.test(o.name)).flatMap(o=>colors(o.value));
    if(!bundle&&chosen.length>1&&currentColors.length){
      const same=chosen.filter(x=>colors(x.label).some(c=>currentColors.includes(c)));
      if(same.length)chosen=same;
      else {const notConflicting=chosen.filter(x=>!colors(x.label).length);if(notConflicting.length)chosen=notConflicting;}
    }
    // For a separately selected blouse, do not add the one-size collar's table.
    // The explicit blouse+collar bundle keeps both parts.
    if(style&&!bundle){
      const size=sizeKey(variant?.options?.find(o=>/size|サイズ/i.test(o.name))?.value||'FREE');
      const exactSize=chosen.filter(x=>(x.table.rows||[]).some(r=>sizeKey(r[0])===size));
      if(exactSize.length)chosen=exactSize;
    }
    return chosen;
  }
  function productParts(chart,variant,title){
    const size=sizeOf(variant),chosen=tablesForVariant(chart,variant),parts=[],evaluations=[];
    for(const {table,index,label} of chosen){
      const rows=(table.rows||[]).filter(r=>Array.isArray(r)&&canonical(r[0])===canonical(size));
      // A one-size component of a set remains its own part; never copy another part's dimensions.
      if(!rows.length&&chosen.length>1&&(table.rows||[]).length===1&&canonical(table.rows[0][0])==='FREE')rows.push(table.rows[0]);
      if(rows.length!==1)continue;
      const raw=rows[0],dimensions=[];
      for(let i=1;i<(table.columns||[]).length;i++){
        const value=text(raw[i]);if(value&&value!=='-'&&value!=='—')dimensions.push({label:heading(table.columns[i]).slice(0,80),value:value.slice(0,100)});
      }
      if(dimensions.length)parts.push({table:index,label:label.slice(0,80),size:text(raw[0]),dimensions});
      const row=parsedRow(table,raw,chart,title);
      const accessoryPart=/マフラー|スカーフ|ベルト|バッグ|帽子|\b(scarf|belt|bag|hat)\b/i.test(label)&&!primaryNames.some(k=>row[k]);
      if(!accessoryPart)evaluations.push({table:index,label,row});
    }
    return {parts,evaluations,expectedParts:chosen.filter(({label,table})=>!(/マフラー|スカーフ|ベルト|バッグ|帽子|\b(scarf|belt|bag|hat)\b/i.test(label)&&!table.columns.some(c=>Object.values(fieldPatterns).slice(0,4).some(re=>re.test(normalizedHeading(heading(c))))))).length};
  }
  function evaluateRow(row,b){
    let score=0,weight=0,tooSmall=false,caution=false,measures=0;
    const use=(name,bodyValue,target,w)=>{
      const range=LondaFitModel.dimension(row,name);if(!range)return;
      measures++;const elasticUnknown=row.elastic?.[name]&&range.min===range.max;
      // Height/weight-derived circumferences are estimates. Only a large shortfall
      // in a published circumference rejects a candidate; unspecified stretch is unknown.
      const shortfall=bodyValue-range.max,limit=name==='waist'?Math.max(10,bodyValue*.16):Math.max(12,bodyValue*.15);
      if(!elasticUnknown&&shortfall>limit)tooSmall=true;
      if(elasticUnknown||shortfall>0||range.max-bodyValue<2)caution=true;
      const circumference=range.max>range.min?mid(range):range.min;
      score+=Math.abs(circumference-bodyValue-target)*w;weight+=w;
    };
    const chest=LondaFitModel.dimension(row,'chest');
    if(chest)use('chest',b.bust,chest.max>chest.min?2:row.kind==='dress'?6:12,1);
    use('waist',b.waist,row.waistMax>row.waist?0:2,.55);
    use('hip',b.hip,row.kind==='bottoms'?6:4,.5);
    const length=LondaFitModel.dimension(row,'length'),shoulder=LondaFitModel.dimension(row,'shoulder'),height=LondaFitModel.dimension(row,'height');
    // Shoulder width and hem length vary intentionally by design, so they never reject.
    if(length)score+=Math.abs(mid(length)-b.height*(row.kind==='dress'?.68:row.kind==='bottoms'?.56:.42))*.04;
    if(shoulder)score+=Math.abs(mid(shoulder)-b.shoulder-2)*.03;
    if(height){score+=Math.max(0,height.min-b.height,b.height-height.max)*.4;caution||=b.height<height.min||b.height>height.max;}
    const core=row.kind==='bottoms'?Boolean(row.waist&&row.hip):Boolean(row.chest);
    return {score:score/Math.max(1,weight),tooSmall,caution,measures,numeric:measures>0||Boolean(length||shoulder||height),complete:core};
  }
  const labelOrder={XXS:-1,XS:0,S:1,M:2,L:3,XL:4,XXL:5,'2XL':5,XXXL:6,'3XL':6,'4XL':7,'5XL':8,'6XL':9};
  const estimatedLabel=b=>2+(b.bust-88)/8+(b.height-165)/50;
  function labelScore(size,b){
    const label=canonical(size);
    if(label==='FREE'||label==='DEFAULTTITLE')return 0;
    if(Object.prototype.hasOwnProperty.call(labelOrder,label)){
      const estimate=estimatedLabel(b);
      return Math.abs(labelOrder[label]-estimate)*4;
    }
    // Product-specific numerical codes are not converted into western size standards.
    return null;
  }
  // Reviewed, ambiguous published dimensions only. These are exact snapshots, not
  // corrected measurements or claimed stretch limits. A later chart edit disables
  // the exception automatically; another product or another part never inherits it.
  const referencePolicies={
    'asymmetric-cut-out-hook-cardigan':{chartType:'tops',part:0,label:'SIZE',type:'tops',headings:['SIZE','着丈','胸囲','肩幅','裄丈'],rows:[['FREE','50-66','62','38','83']]},
    'flower-gradient-dress':{chartType:'dress',part:0,label:'SIZE',type:'dress',headings:['SIZE','着丈','胸囲','ウエスト','ヒップ'],rows:[['S','130','68','60','76'],['M','131','72','64','80'],['L','132','76','68','84']]},
    'drape-wrapped-off-shoulder-set-series':{chartType:'multi',part:1,label:'トップス',type:'tops',headings:['トップス','着丈','胸囲','袖丈'],rows:[['S','48','60','62-75'],['M','50','64','64-77'],['L','52','68','66-79']]},
    'urban-veil-series':{chartType:'multi',part:1,label:'ベスト',type:'tops',headings:['ベスト','着丈','胸囲','肩幅'],rows:[['FREE','56','54','53']]},
    'neon-drift-series':{chartType:'multi',part:1,label:'トップス',type:'tops',headings:['トップス','着丈','胸囲','肩幅','袖丈'],rows:[['FREE','47','66','25','46']]}
  };
  function referenceFallback(candidates,{productHandle,chart,stock,body,reference}){
    if(candidates.some(c=>c.eligible)||!candidates.length)return candidates;
    const policy=referencePolicies[text(productHandle)];
    if(!policy||chart?.unit!=='cm'||chart.type!==policy.chartType)return candidates;
    const fingerprint=JSON.stringify([[policy.part,policy.label,policy.type,policy.headings,policy.rows]]);
    const matching=candidates.filter(c=>{
      const variant=stock.find(v=>String(v.id)===c.variantId);
      if(!variant||!c.tooSmall||c.parts.length!==1||c.parts[0].table!==policy.part)return false;
      const tables=tablesForVariant(chart,variant).map(({table,index,label})=>[index,label,table.type,(table.columns||[]).map(heading),(table.rows||[]).map(row=>Array.isArray(row)?row.map(text):null)]);
      return JSON.stringify(tables)===fingerprint&&labelScore(c.size,body)!==null;
    });
    if(!matching.length)return candidates;
    const estimate=estimatedLabel(body),ranks=matching.map(c=>canonical(c.size)==='FREE'?null:labelOrder[canonical(c.size)]).filter(Number.isFinite);
    // This is a conservative limit on an uncertain suggestion, not a garment's
    // verified body-size range. Use the existing label estimate, never height alone.
    // FREE has no standard capacity: do not extend a reference suggestion to a
    // markedly larger body. 180cm/72kg remains possible; 180cm/100kg does not.
    const low=ranks.length?Math.min(...ranks)-1:-1,high=ranks.length?Math.max(...ranks)+1:4;
    if(estimate<low||estimate>high)return candidates;
    return candidates.map(c=>{
      if(!matching.includes(c))return c;
      const modelMatch=Boolean(reference&&canonical(reference.size)===canonical(c.size)&&Math.abs(reference.height-body.height)<=10);
      const score=labelScore(c.size,body)-(modelMatch?Math.max(0,3-Math.abs(reference.height-body.height)*.3):0);
      // Keep every original row and image-prompt part. Eligibility here means a
      // low-confidence reference is available, not that these dimensions fit.
      return {...c,score,basis:modelMatch?'model':'label',caution:true,referenceFallback:true,eligible:true,tooSmall:false};
    });
  }
  function recommendForProduct({chart,title='',tags=[],variants=[],currentVariantId,height,weight,modelText='',productHandle=''}={}){
    const h=Number(height),w=Number(weight),empty={best:null,candidates:[],reference:null,body:null};
    if(!Number.isFinite(h)||!Number.isFinite(w)||h<130||h>220||w<30||w>200||!isApparel(chart,title,tags))return {status:'invalid',...empty};
    const b=LondaFitModel.body(h,w),base={...empty,body:b};
    if(!Array.isArray(variants)||!variants.length)return {status:'no_stock',...base};
    const current=currentVariantId?variants.find(v=>String(v.id)===String(currentVariantId)):(variants.find(v=>v.available===true)||variants[0]);
    if(!current)return {status:'invalid',...base};
    const nonSize=v=>JSON.stringify((v.options||[]).filter(o=>!/size|サイズ/i.test(o.name)).map(o=>[text(o.name).toLowerCase(),text(o.value)]).sort((a,b)=>a[0].localeCompare(b[0])));
    const sameOptions=nonSize(current),stock=variants.filter(v=>v.available===true&&nonSize(v)===sameOptions);
    if(!stock.length)return {status:'no_stock',...base};
    const sizeRows=variants.filter(v=>nonSize(v)===sameOptions).map(v=>({size:sizingLabel(sizeOf(v))}));
    const reference=LondaFitModel.modelReference(String(modelText).replace(/<[^>]*>/g,' '),sizeRows);
    base.reference=reference;
    const useMeasurements=!chart?.unit||chart.unit==='cm';
    let candidates=stock.map(variant=>{
      const size=sizeOf(variant),parsed=useMeasurements?productParts(chart,variant,title):{parts:[],evaluations:[],expectedParts:0};
      const evaluations=parsed.evaluations.map(x=>evaluateRow(x.row,b)),anyNumeric=evaluations.some(x=>x.numeric),measures=evaluations.reduce((n,x)=>n+x.measures,0);
      const complete=evaluations.length>0&&evaluations.length===parsed.expectedParts&&evaluations.every(x=>x.complete);
      const modelMatch=Boolean(reference&&canonical(reference.size)===canonical(size)&&Math.abs(reference.height-h)<=10);
      const label=labelScore(size,b);
      const sufficient=anyNumeric||modelMatch||label!==null;
      const basis=measures?(complete?'measurements':'partial'):anyNumeric?'partial':modelMatch?'model':'label';
      let score=evaluations.reduce((sum,x)=>sum+x.score,0)/Math.max(1,evaluations.length);
      if(!measures&&label!==null)score+=label;
      // Model height has no weight implication. It resolves close choices only.
      if(modelMatch)score-=Math.max(0,3-Math.abs(reference.height-h)*.3);
      const out={size,variantId:String(variant.id),parts:parsed.parts,basis,caution:!complete||evaluations.some(x=>x.caution)||basis!=='measurements',score,
        eligible:sufficient&&!evaluations.some(x=>x.tooSmall),tooSmall:evaluations.some(x=>x.tooSmall)};
      if(parsed.evaluations.length===1)out.row=parsed.evaluations[0].row;
      return out;
    });
    candidates=referenceFallback(candidates,{productHandle,chart,stock,body:b,reference});
    candidates.sort((a,z)=>Number(z.eligible)-Number(a.eligible)||a.score-z.score);
    const best=candidates.find(x=>x.eligible)||null;
    return {status:best?'recommended':candidates.every(x=>x.tooSmall)?'too_small':'insufficient',best,candidates,reference,body:b};
  }
  return {isApparel,sizeOf,variantForSize,selection,tablesForVariant,recommendForProduct};
})();



